export type ButtonPaintMapType = {
  primary: string;
  secondary: string;
  tertiary: string;
  light: string;
  ghost: string;
  nav: string;
  grey: string;
  // Status colors
  success: string;
  error: string;
  warning: string;
  info: string;
  // Base colors
  green: string;
  red: string;
  blue: string;
  orange: string;
  yellow: string;
  purple: string;
  pink: string;
  indigo: string;
  // Dark variants (900 base, 950 hover, 800 pressed)
  darkPrimary: string;
  darkSecondary: string;
  darkGray: string;
  darkSuccess: string;
  darkError: string;
  darkWarning: string;
  darkInfo: string;
  darkGreen: string;
  darkRed: string;
  darkBlue: string;
  darkOrange: string;
  darkYellow: string;
  darkPurple: string;
  darkPink: string;
  darkIndigo: string;
};
export type ButtonPaintMapKeyType = keyof ButtonPaintMapType;

export const buttonPaintMap: ButtonPaintMapType = {
  primary: "primary.solid",
  secondary: "secondary.solid",
  tertiary: "gray.600",
  light: "gray.100",
  ghost: "transparent",
  nav: "transparent",
  grey: "gray.solid",
  // Status colors - use semantic tokens
  success: "success.solid",
  error: "error.solid",
  warning: "warning.solid",
  info: "info.solid",
  // Base colors - use .solid semantic tokens
  green: "green.solid",
  red: "red.solid",
  blue: "blue.solid",
  orange: "orange.600",
  yellow: "yellow.solid",
  purple: "purple.solid",
  pink: "pink.solid",
  indigo: "indigo.solid",
  // Dark variants - use .solidDark semantic tokens (900 shade)
  darkPrimary: "primary.solidDark",
  darkSecondary: "secondary.solidDark",
  darkGray: "gray.solidDark",
  darkSuccess: "success.solidDark",
  darkError: "error.solidDark",
  darkWarning: "warning.solidDark",
  darkInfo: "info.solidDark",
  darkGreen: "green.solidDark",
  darkRed: "red.solidDark",
  darkBlue: "blue.solidDark",
  darkOrange: "orange.900",
  darkYellow: "yellow.solidDark",
  darkPurple: "purple.solidDark",
  darkPink: "pink.solidDark",
  darkIndigo: "indigo.solidDark",
};

export const buttonPaintMapDisabled: ButtonPaintMapType = {
  primary: "primary.disabled",
  secondary: "secondary.disabled",
  tertiary: "gray.500",
  light: "gray.500",
  ghost: "gray.500",
  nav: "gray.500",
  grey: "gray.disabled",
  // Status colors
  success: "success.disabled",
  error: "error.disabled",
  warning: "warning.disabled",
  info: "info.disabled",
  // Base colors
  green: "green.disabled",
  red: "red.disabled",
  blue: "blue.disabled",
  orange: "orange.400",
  yellow: "yellow.disabled",
  purple: "purple.disabled",
  pink: "pink.disabled",
  indigo: "indigo.disabled",
  // Dark variants - use .disabledDark semantic tokens (700 shade)
  darkPrimary: "primary.disabledDark",
  darkSecondary: "secondary.disabledDark",
  darkGray: "gray.disabledDark",
  darkSuccess: "success.disabledDark",
  darkError: "error.disabledDark",
  darkWarning: "warning.disabledDark",
  darkInfo: "info.disabledDark",
  darkGreen: "green.disabledDark",
  darkRed: "red.disabledDark",
  darkBlue: "blue.disabledDark",
  darkOrange: "orange.700",
  darkYellow: "yellow.disabledDark",
  darkPurple: "purple.disabledDark",
  darkPink: "pink.disabledDark",
  darkIndigo: "indigo.disabledDark",
};

export const buttonPaintMapActive: ButtonPaintMapType = {
  primary: "primary.pressed",
  secondary: "secondary.pressed",
  tertiary: "gray.400",
  light: "gray.400",
  ghost: "gray.400",
  nav: "gray.400",
  grey: "gray.pressed",
  // Status colors
  success: "success.pressed",
  error: "error.pressed",
  warning: "warning.pressed",
  info: "info.pressed",
  // Base colors
  green: "green.pressed",
  red: "red.pressed",
  blue: "blue.pressed",
  orange: "orange.800",
  yellow: "yellow.pressed",
  purple: "purple.pressed",
  pink: "pink.pressed",
  indigo: "indigo.pressed",
  // Dark variants - use .pressedDark semantic tokens (800 shade)
  darkPrimary: "primary.pressedDark",
  darkSecondary: "secondary.pressedDark",
  darkGray: "gray.pressedDark",
  darkSuccess: "success.pressedDark",
  darkError: "error.pressedDark",
  darkWarning: "warning.pressedDark",
  darkInfo: "info.pressedDark",
  darkGreen: "green.pressedDark",
  darkRed: "red.pressedDark",
  darkBlue: "blue.pressedDark",
  darkOrange: "orange.500",
  darkYellow: "yellow.pressedDark",
  darkPurple: "purple.pressedDark",
  darkPink: "pink.pressedDark",
  darkIndigo: "indigo.pressedDark",
};

export const buttonPaintMapHover: ButtonPaintMapType = {
  primary: "primary.emphasized",
  secondary: "secondary.emphasized",
  tertiary: "gray.800",
  light: "gray.200",
  ghost: "gray.200",
  nav: "gray.200",
  grey: "gray.emphasized",
  // Status colors
  success: "success.emphasized",
  error: "error.emphasized",
  warning: "warning.emphasized",
  info: "info.emphasized",
  // Base colors
  green: "green.emphasized",
  red: "red.emphasized",
  blue: "blue.emphasized",
  orange: "orange.700",
  yellow: "yellow.emphasized",
  purple: "purple.emphasized",
  pink: "pink.emphasized",
  indigo: "indigo.emphasized",
  // Dark variants - use .emphasizedDark semantic tokens (950 shade)
  darkPrimary: "primary.emphasizedDark",
  darkSecondary: "secondary.emphasizedDark",
  darkGray: "gray.emphasizedDark",
  darkSuccess: "success.emphasizedDark",
  darkError: "error.emphasizedDark",
  darkWarning: "warning.emphasizedDark",
  darkInfo: "info.emphasizedDark",
  darkGreen: "green.emphasizedDark",
  darkRed: "red.emphasizedDark",
  darkBlue: "blue.emphasizedDark",
  darkOrange: "orange.400",
  darkYellow: "yellow.emphasizedDark",
  darkPurple: "purple.emphasizedDark",
  darkPink: "pink.emphasizedDark",
  darkIndigo: "indigo.emphasizedDark",
};

export const buttonPaintTextColor: ButtonPaintMapType = {
  primary: "primary.contrast",
  secondary: "secondary.contrast",
  tertiary: "white",
  light: "gray.900",
  ghost: "fg",
  nav: "fg",
  grey: "white",
  // Status colors
  success: "success.contrast",
  error: "error.contrast",
  warning: "warning.contrast",
  info: "info.contrast",
  // Base colors
  green: "white",
  red: "white",
  blue: "white",
  orange: "white",
  yellow: "gray.900", // Yellow needs dark text for contrast
  purple: "white",
  pink: "white",
  indigo: "white",
  // Dark variants - use contrastDark (white in light mode, dark in dark mode)
  darkPrimary: "primary.contrastDark",
  darkSecondary: "secondary.contrastDark",
  darkGray: "gray.contrastDark",
  darkSuccess: "success.contrastDark",
  darkError: "error.contrastDark",
  darkWarning: "warning.contrastDark",
  darkInfo: "info.contrastDark",
  darkGreen: "green.contrastDark",
  darkRed: "red.contrastDark",
  darkBlue: "blue.contrastDark",
  darkOrange: "white",
  darkYellow: "yellow.contrastDark",
  darkPurple: "purple.contrastDark",
  darkPink: "pink.contrastDark",
  darkIndigo: "indigo.contrastDark",
};
